/**
 * @ngdoc service
 * @name $GbAuctionMetaService
 * @description Provides ability to update the gb meta tags
 *
 * If no content for a tag is present, it will revert back to the default tags
 **/

import { Inject, Injectable } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { AuctionEntity } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.models'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { BehaviorSubject, Observable, combineLatest, map, of, switchMap } from 'rxjs'
import { gbTag, UseTagsEnum } from './gb-auction-meta.models'
import { SelectedItemFacade } from 'libs/gb-front/selected-item/data-access/src/lib/+state/selected-item.facade'
import { EventTicketPackagesFacade } from 'libs/gb-front/event-tickets/data-access-event-ticket-packages/src/lib/+state/event-ticket-packages.facade'
import { EventTicketsConstants } from 'libs/gb-front/event-tickets/feature/src/lib/event-tickets-constants'

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class GbAuctionMetaService {
  //TODO (GBX-411) use app config to construct default meta image url, this will needed to be uploaded to the correct s3 bucket

  defaultTags: gbTag[] = [
    {
      property: 'google_signin',
      content: '823678877303-cie8l6o54vjmdstmrils0cvodjhhov49.apps.googleusercontent.com',
    },
    {
      property: 'fb_app',
      content: '239802952849466',
    },
    {
      property: 'title',
      content: 'GalaBid - Silent Auction Software, Fundraising for Charity',
    },
    {
      property: 'image',
      content:
        'https://s3-ap-southeast-2.amazonaws.com/cc.events/crowdcomms-galabid-branding/gb_fundraising-platform_global-metaimage.jpg',
    },
    {
      property: 'url',
      content: 'https://app.galabid.com',
    },
    {
      property: 'type',
      content: 'website',
    },
    {
      property: 'description',
      content:
        'Raise money for school, charity & non-profit organisations online through silent auction software, raffle, donations and auction services platforms, including items on consignment.',
    },
  ]

  defaultTags$ = of(this.defaultTags)
  titlePrefix = 'GalaBid'
  defaultTitle = 'GalaBid'

  /** Subject to decide whether to use the auction, default or the item for the meta tags and title*/

  useTagSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(UseTagsEnum.Default)

  useTagSubjectObservable$ = this.useTagSubject$.asObservable()

  auctionTags$: Observable<gbTag[]> = this.auctionFacade.auction$.pipe(
    map((auction: AuctionEntity) => {
      return [
        {
          property: 'title',
          content: `${auction.name} - Fundraising Campaign via GalaBid`,
        },
        {
          property: 'description',
          content: `Support our campaign - ${auction.name}. Fundraising, Silent Auction, Raffle and Donations.`,
        },
        {
          property: 'url',
          content: `${this.appConfig.appUrl}/${auction.url_name}`,
        },
        {
          property: 'image',
          content: auction.banners[0] && auction.banners[0].image.image,
        },
      ]
    }),
  )

  itemTags$: Observable<gbTag[]> = combineLatest([
    this.selectedItemFacade.selectedItem$,
    this.auctionFacade.auctionUrlName$,
  ]).pipe(
    map(([item, urlName]) => {
      let itemTags: gbTag[] = []
      if (item) {
        itemTags = [
          {
            property: 'title',
            content: item.name,
          },
          {
            property: 'description',
            content: item.description,
          },
          {
            property: 'url',
            content: `${this.appConfig.appUrl}/${urlName}/items/${item.uuid}`,
          },
          {
            property: 'image',
            content: item.images[0] && item.images[0].image.image,
          },
        ]
      }
      return itemTags
    }),
  )

  itemsTags$: Observable<gbTag[]> = this.auctionFacade.auction$.pipe(
    map((auction) => {
      const itemsTags = [
        {
          property: 'title',
          content: `All Items - ${auction.name} | GalaBid`,
        },
        {
          property: 'description',
          content: `Explore all items of our campaign - ${auction.name}. Fundraising, Silent Auction, Raffle and Donations.`,
        },
      ]
      return itemsTags
    }),
  )

  eventTicketTags$: Observable<gbTag[]> = combineLatest([
    this.eventTicketPackagesFacade.selectedEventTicketPackage$,
    this.auctionFacade.auctionUrlName$,
  ]).pipe(
    map(([eventTicket, urlName]) => {
      let eventTicketTags: gbTag[] = []
      if (eventTicket) {
        eventTicketTags = [
          {
            property: 'title',
            content: eventTicket.name,
          },
          {
            property: 'description',
            content: eventTicket.description,
          },
          {
            property: 'url',
            content: `${this.appConfig.appUrl}/${urlName}/${EventTicketsConstants.EVENT_TICKETS}/${eventTicket.uuid}`,
          },
          {
            property: 'image',
            content: eventTicket.images[0] && eventTicket.images[0].image.image,
          },
        ]
      }
      return eventTicketTags
    }),
  )

  eventTicketsTags$: Observable<gbTag[]> = this.auctionFacade.auction$.pipe(
    map((auction) => {
      const eventTicketsTags = [
        {
          property: 'title',
          content: `Event Tickets - ${auction.name} | GalaBid`,
        },
        {
          property: 'description',
          content: `Explore tickets to attend our event - ${auction.name}. Fundraising, Silent Auction, Raffle and Donations.`,
        },
      ]
      return eventTicketsTags
    }),
  )

  registerTags$: Observable<gbTag[]> = this.auctionFacade.auction$.pipe(
    map((auction) => {
      let registerTags = [
        {
          property: 'title',
          content: `Register for ${auction.name} | GalaBid`,
        },
        {
          property: 'description',
          content: `Register for our campaign - ${auction.name}. Fundraising, Silent Auction, Raffle and Donations.`,
        },
      ]
      return registerTags
    }),
  )

  loginTags$: Observable<gbTag[]> = this.auctionFacade.auction$.pipe(
    map((auction) => {
      let registerTags = [
        {
          property: 'title',
          content: `Login - ${auction.name} | GalaBid`,
        },
        {
          property: 'description',
          content: `Login to our campaign - ${auction.name}. Fundraising, Silent Auction, Raffle and Donations.`,
        },
      ]
      return registerTags
    }),
  )

  tagChanges$ = this.useTagSubject$.asObservable().pipe(
    switchMap((useTag) => {
      return this.getTags(useTag)
    }),
    map((tags) => {
      this.setTags(tags)
      this.setTitleFromTags(tags)
    }),
  )

  constructor(
    private metaTagService: Meta,
    private auctionFacade: AuctionFacade,
    private selectedItemFacade: SelectedItemFacade,
    private eventTicketPackagesFacade: EventTicketPackagesFacade,
    private titleService: Title,
    @Inject(APP_CONFIG) protected appConfig: any,
  ) {
    this.tagChanges$.pipe(untilDestroyed(this)).subscribe()
  }

  getTags(useTag: string): Observable<gbTag[]> {
    switch (useTag) {
      case UseTagsEnum.Auction:
        return this.auctionTags$
      case UseTagsEnum.Item:
        return this.itemTags$
      case UseTagsEnum.Items:
        return this.itemsTags$
      case UseTagsEnum.EventTicket:
        return this.eventTicketTags$
      case UseTagsEnum.EventTickets:
        return this.eventTicketsTags$
      case UseTagsEnum.Register:
        return this.registerTags$
      case UseTagsEnum.Login:
        return this.loginTags$
    }

    return this.defaultTags$
  }

  setTags(data: gbTag[]): void {
    let tags: gbTag[] = []

    for (let i = 0; i < data.length; i++) {
      if (data[i].content) {
        tags.push(data[i])
      } else {
        let defaultTag = this.findDefaultTag(data[i].property)
        defaultTag ? tags.push(defaultTag) : null
      }
    }

    this.addTags(tags)
  }

  setTitleFromTags(tags: gbTag[]) {
    const title = tags.find((tag) => tag.property == 'title')
    if (title && title.content) {
      this.titleService.setTitle(title.content)
    } else {
      this.titleService.setTitle(this.defaultTitle)
    }
  }

  findDefaultTag(property: string): gbTag | undefined {
    return this.defaultTags.find((tag) => tag.property == property)
  }

  addTags(tags: gbTag[]): void {
    for (let i = 0; i < tags.length; i++) {
      let tag: gbTag = tags[i]

      !!this.metaTagService.addTag({ property: `og:${tag.property}` })
        ? this.updateTag(tag.property, tag.content)
        : this.addTag(tag.property, tag.content)
    }
  }

  updateTag(property: string, content: string | undefined): void {
    this.metaTagService.updateTag({
      property: `og:${property}`,
      content: `${content}`,
    })
  }

  addTag(property: string, content: string | undefined): void {
    this.metaTagService.addTag({
      property: `og:${property}`,
      content: `${content}`,
    })
  }
}
